import { MW_STAGE_DIVISOR, type CellViewWire, type PhaseState } from '@badger-sentinel/protocol';
import { useEffect, useMemo, useRef } from 'react';

import { EventRail } from './EventRail.js';
import { RecognitionTable } from './RecognitionTable.js';
import { TimelineDock } from './TimelineDock.js';
import { CHIP } from './chrome.js';
import { TrackReading } from './TrackPanel.js';
import { MwMap, useMwMapAvailable } from '../maps/MwMap.js';
import {
  mwClockSeconds,
  mwMapRecords,
  type MwGeometry,
  type MwWindowSpan,
} from '../model/mw-map-records.js';
import { trackGroups, trackRow, type MwTrackRow, type MwTrackWire } from '../model/mw.js';
import { useConsoleStore } from '../state/store.js';
import { DATA, DATA_LABEL, LABEL } from '../../theme/type.js';

/**
 * The Missile Warning centre pane (design 726–1135).
 *
 * Three bands and a rail: the clock header, the launch-event rail beside the map,
 * and the timeline dock. The track's **identity, state and actions** are not here
 * — those are a selection, so they live in the console's right rail with every
 * other selection (`mw/TrackPanel.tsx`). The design gives the MW view its own two
 * rails because the prototype's MW screen replaced the whole body; ours keeps the
 * frame, which is shell.md's split and the reason the console is one shape on
 * every tab.
 *
 * ## What the map's absence is allowed to change, and what it is not
 *
 * While `MwMap.MW_MAP_ENABLED` is false the pane has a column with nothing in it,
 * and the rail used to take the whole board: 640 px at the 1280 floor holding
 * 144 px of ink, beside a right rail whose 203 px of scroll was hiding 623 px of
 * the reading at stage 4. So the pane draws the **reading** — the credit ladder
 * and the staged observations — and the rail stands its copy down
 * (`TrackPanel`'s `readingInPane`, decided by the shell because only the shell
 * knows which tab is up).
 *
 * The frame does not change. The rail is still a column, the selection still
 * lives in the right rail, and when the map returns the board goes back to
 * rail-plus-map with one boolean.
 *
 * ## Every clock here is the corrected one
 *
 * `serverNowMs` comes down from `useServerNow` — the single place in the console
 * that reads a local clock and the only one that adds the offset. Nothing in this
 * subtree can reach `Date.now()`, which is structural rather than remembered: a
 * laptop 40 s fast would otherwise show a stage as due when it has already
 * landed, and the operator would wait for data that is already on their screen.
 *
 * ## The window span, derived rather than guessed
 *
 * The timeline and the map's clock both need "when did this window open", and no
 * field on the wire says so. It is nonetheless **exact**, from two things the
 * client already has: `phase.deadlineAtMs`, and any one remaining stage time.
 * `mwStageTimes` puts stage *n* at `anchor + n × ordersMs / MW_STAGE_DIVISOR`,
 * and `MW_STAGE_DIVISOR` is a protocol constant — so one entry plus the deadline
 * solves for both the step and the anchor. See `spanOf`.
 *
 * That matters more than it looks: the obvious reading — "the earliest time still
 * on the schedule" — moves later every time a stage fires, so every lane would
 * silently rescale under the operator at the exact moment they were reading it.
 *
 * Once stage 4 has landed the schedule is empty and there is nothing left to
 * solve from, so the answer is **kept for the window it belongs to**. The window's
 * geometry does not change inside a window; forgetting it would take the timeline
 * off the screen at the moment the last stage arrives, which is the moment the
 * operator is deciding.
 */

export interface MwViewProps {
  readonly view: CellViewWire;
  readonly phase: PhaseState | null;
  readonly serverNowMs: number;
  readonly onAction: (
    eventId: string,
    action: 'IDENTIFY' | 'RELEASE' | 'DISMISS',
    identity?: string,
  ) => void;
  /**
   * Region id → `[lon, lat]`, from the scenario's map pack (M5-D11), or `null`
   * where there is none.
   *
   * The warning picture is drawn over the theatre's own geography — `mw-map.js`
   * extends `region-map` — and `MwTrackView` carries `RegionId`s, so this is the
   * whole of what Phase 4 was missing. It is the shell's because the same facade
   * feeds both boards.
   */
  readonly geometry: MwGeometry | null;
}

/** The launch-event rail's width. Narrow: the board is the map and the dock. */
export const MW_RAIL_WIDTH = 232;

/**
 * The rail's width when there is no map beside it.
 *
 * Wider than `MW_RAIL_WIDTH`, because at 232 the identity elides beside the
 * open-track marker and the identity is what an operator says out loud — and
 * far narrower than the whole pane, which is what it used to take. Measured at
 * the 1280 floor, a full-pane rail was 640 px holding 144 px of ink: three
 * quarters of the board's largest region was empty while the reading it exists
 * to support was hidden in a 203 px scroll. The rest of the pane now draws the
 * reading.
 */
export const MW_RAIL_WIDTH_ALONE = 320;

/**
 * The board's floor.
 *
 * At the 1280 × 720 minimum (shell.md §Layout) the chrome and the queue dock take
 * about 280 px, so this is a little more than what is left — the pane scrolls the
 * last few pixels rather than crushing the timeline dock, which is the band the
 * whole phase is about.
 */
export const MW_MIN_HEIGHT = 420;

export function MwView(props: MwViewProps): JSX.Element {
  const selected = useConsoleStore((state) => state.mwSelected);
  const select = useConsoleStore((state) => state.select);
  const selectTrack = useConsoleStore((state) => state.selectMwTrack);
  const sort = useConsoleStore((state) => state.mwSort);
  const setSort = useConsoleStore((state) => state.setMwSort);
  const timelineOpen = useConsoleStore((state) => state.mwTimelineOpen);
  const setTimelineOpen = useConsoleStore((state) => state.setMwTimelineOpen);
  const refOpen = useConsoleStore((state) => state.mwRefOpen);
  const setRefOpen = useConsoleStore((state) => state.setMwRefOpen);

  const tracks = props.view.mwTracks;
  const mapAvailable = useMwMapAvailable();
  const solved = useMemo(() => spanOf(props.view, props.phase), [props.view, props.phase]);
  const span = keepSpan(useRef<Kept>(null), solved, props.phase?.deadlineAtMs ?? null);

  const rows: readonly MwTrackRow[] = useMemo(
    () => tracks.map((track) => trackRow(track, props.serverNowMs, span)),
    [tracks, props.serverNowMs, span],
  );

  // The map facade (Phase 0's finding 1): records in the vendored renderer's own
  // vocabulary, not a React view model. Geometry is a map pack keyed by scenario
  // (M5-D11); without one this is empty and the pane says so rather than drawing
  // an empty world.
  const geometry = props.geometry;
  const records = useMemo(
    () =>
      mwMapRecords(props.view, {
        geometry,
        selected,
        span,
      }),
    [props.view, geometry, selected, span],
  );

  const nearest = rows
    .map((row) => row.next)
    .filter((next): next is NonNullable<typeof next> => next !== null)
    .sort((a, b) => a.inMs - b.inMs)[0];

  const onSelect = (eventId: string): void => {
    selectTrack(eventId);
    // The right rail is where the detail is, so selecting a track opens it — the
    // same rule `select` already applies to every other subject.
    select({ kind: 'MW_TRACK', id: eventId });
  };

  const selectedTrack = tracks.find((track) => track.eventId === selected) ?? null;

  // The keyboard handler below is bound once against the rail's order, not
  // rebuilt on every render just because `onSelect` is a new closure each time.
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;

  /*
   * One track, nothing selected: select it.
   *
   * Measured with six seconds left in a window — the rail said `NOTHING
   * SELECTED`, the row said `OWES A DECISION` and the commit said the same,
   * and the only thing between the operator and every control they needed was a
   * click on a list of one that nobody had told them to make.
   *
   * Deliberately only when there is exactly one. Choosing between two is a
   * decision, and this seat's whole discipline is not making the operator's
   * decisions for them. It never fights a selection either: the guard is that
   * nothing at all is selected.
   */
  const onlyTrack = tracks.length === 1 ? tracks[0] : undefined;
  useEffect(() => {
    if (selected !== null || onlyTrack === undefined) return;
    selectTrack(onlyTrack.eventId);
    select({ kind: 'MW_TRACK', id: onlyTrack.eventId });
  }, [selected, onlyTrack, selectTrack, select]);

  /*
   * The board's three keys (`MW_HOTKEYS`), bound here rather than in the shell.
   *
   * Stepping the list has to walk it in the order the rail *draws* it — groups
   * first, then the live sort — and that order is `trackGroups`, which is
   * computed in `EventRail` from state this component owns. Reproducing it in
   * the shell would mean rebuilding the rows and the window span a second time,
   * and a second copy of a sort is a second copy that can disagree.
   */
  const railOrder = useMemo(
    () => trackGroups(rows, sort).flatMap((group) => group.rows.map((row) => row.eventId)),
    [rows, sort],
  );
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target;
      if (target instanceof HTMLElement) {
        if (target.isContentEditable) return;
        if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return;
      }
      const key = event.key.toLowerCase();
      if (key === 'n') {
        event.preventDefault();
        setRefOpen(!refOpen);
        return;
      }
      if (key === 'w') {
        event.preventDefault();
        setTimelineOpen(!timelineOpen);
        return;
      }
      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;
      if (railOrder.length === 0) return;
      event.preventDefault();
      const at = selected === null ? -1 : railOrder.indexOf(selected);
      // From nothing, Down takes the first and Up takes the last. Stepping stops
      // at the ends rather than wrapping: a list of one that cycles under a key
      // reads as a control that did nothing.
      const next =
        at === -1
          ? ((event.key === 'ArrowDown' ? railOrder[0] : railOrder[railOrder.length - 1]) ?? null)
          : (railOrder[
              Math.min(railOrder.length - 1, Math.max(0, at + (event.key === 'ArrowDown' ? 1 : -1)))
            ] ?? null);
      if (next !== null && next !== selected) onSelectRef.current(next);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [railOrder, selected, refOpen, setRefOpen, timelineOpen, setTimelineOpen]);

  return (
    <div
      data-testid="mw-view"
      style={{
        position: 'relative',
        // `1 1 auto` with a floor, not `1 1 0%`. It was written when the centre
        // pane's scroll column still carried the M2 harness below this board and a
        // zero basis let the widget squeeze it to nothing — present in the DOM and
        // invisible on screen. **Phase 9 retired the harness and left this alone**:
        // the floor is still what stops a short viewport collapsing the board, and
        // the pane's column scrolls instead. Kept, because the reason changed and
        // the behaviour did not.
        flex: '1 1 auto',
        minHeight: MW_MIN_HEIGHT,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--canvas)',
      }}
    >
      <div
        data-testid="mw-clock-header"
        data-mat="chrome hdr"
        style={{
          flex: 'none',
          height: 33,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '0 12px',
          background: 'var(--surface-2)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        {/* Not a second countdown to the deadline — the status row above already
            owns that. This one names the next thing that will *change the board*,
            which is the only clock the MW seat is actually working to. */}
        <span style={{ ...DATA_LABEL, flex: 'none', color: 'var(--text-muted)' }}>NEXT DATA</span>
        <span
          data-testid="mw-next-data"
          style={{
            flex: 'none',
            fontFamily: 'var(--font-data)',
            fontSize: 15,
            fontWeight: 700,
            letterSpacing: '.04em',
            color: nearest === undefined ? 'var(--text-disabled)' : 'var(--text)',
          }}
        >
          {nearest === undefined ? '—' : nearest.clock}
        </span>
        {nearest !== undefined && (
          <span
            data-testid="mw-next-label"
            style={{ ...DATA, flex: 'none', fontWeight: 400, color: 'var(--text-muted)' }}
          >
            STAGE {nearest.stage}
          </span>
        )}
        <span style={{ flex: 1 }} />
        <button
          type="button"
          data-testid="mw-timeline-toggle"
          aria-pressed={timelineOpen}
          onClick={() => setTimelineOpen(!timelineOpen)}
          style={CHIP}
        >
          TIMELINE
        </button>
        <button
          type="button"
          data-testid="mw-recognition-open"
          // Its neighbour has carried `aria-pressed` since it was written and
          // this one carried nothing, so the same band announced two identical
          // controls under two different contracts.
          aria-expanded={refOpen}
          onClick={() => setRefOpen(true)}
          style={CHIP}
        >
          RECOGNITION
        </button>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: 'flex', gap: 1, background: 'var(--border)' }}>
        {/* The rail is a column either way. Beside a map it is 232; beside the
            reading it is 320, which is what the identity needs before it elides.
            It is never the whole board again. */}
        <EventRail
          rows={rows}
          sort={sort}
          onSort={setSort}
          selected={selected}
          onSelect={onSelect}
          width={mapAvailable ? MW_RAIL_WIDTH : MW_RAIL_WIDTH_ALONE}
        />
        {mapAvailable ? (
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
            <MwMap
              records={records}
              clockSeconds={mwClockSeconds(span, props.serverNowMs)}
              running={props.phase?.status === 'RUNNING'}
              onSelect={(eventId) => {
                if (eventId !== null) onSelect(eventId);
              }}
            />
          </div>
        ) : (
          <ReadingPane track={selectedTrack} serverNowMs={props.serverNowMs} />
        )}
      </div>

      {timelineOpen && (
        <TimelineDock
          rows={rows}
          span={span}
          serverNowMs={props.serverNowMs}
          selected={selected}
          onSelect={onSelect}
        />
      )}

      {refOpen && (
        <RecognitionTable
          catalogue={props.view.mwCatalogue}
          track={selectedTrack}
          onClose={() => setRefOpen(false)}
        />
      )}
    </div>
  );
}

/**
 * The window every lane is drawn across, solved from the schedule and the deadline.
 *
 * `mwStageTimes` is `atMs = anchor + stage × step` with `step = ordersMs /
 * MW_STAGE_DIVISOR`, and the deadline is `anchor + ordersMs`. One remaining entry
 * is therefore enough:
 *
 * ```
 * step   = (deadline − atMs) / (MW_STAGE_DIVISOR − stage)
 * anchor = deadline − MW_STAGE_DIVISOR × step
 * ```
 *
 * `MW_STAGE_DIVISOR − stage` is at least two, because a scheduled stage is never
 * above four. This is arithmetic on shared constants, not a reconstruction of the
 * server's timer: if RB §5.3.4's divisor ever changes, both sides change with the
 * constant.
 *
 * A board with nothing scheduled — every stage in, or a briefed track that never
 * had any — has no span, and the dock is absent rather than drawn against a
 * window it cannot place.
 */
function spanOf(view: CellViewWire, phase: PhaseState | null): MwWindowSpan | null {
  const deadline = phase?.deadlineAtMs ?? null;
  if (deadline === null) return null;
  const entry = view.mwTracks
    .flatMap((track) => track.stageSchedule)
    .filter((candidate) => candidate.stage < MW_STAGE_DIVISOR)
    .sort((a, b) => a.atMs - b.atMs)[0];
  if (entry === undefined) return null;
  const step = (deadline - entry.atMs) / (MW_STAGE_DIVISOR - entry.stage);
  const from = deadline - MW_STAGE_DIVISOR * step;
  return from >= deadline ? null : { fromMs: from, toMs: deadline, stepMs: step };
}

type Kept = { readonly deadline: number; readonly span: MwWindowSpan } | null;

/**
 * The span for **this** window, remembered.
 *
 * Keyed on the deadline, so a new window solves afresh and last window's geometry
 * can never be drawn under this window's marks. A ref rather than store state: it
 * is derived, it is worth nothing after the window closes, and it must not
 * survive a seat switch.
 */
function keepSpan(
  ref: { current: Kept },
  solved: MwWindowSpan | null,
  deadline: number | null,
): MwWindowSpan | null {
  if (deadline === null) return null;
  if (solved !== null) {
    ref.current = { deadline, span: solved };
    return solved;
  }
  return ref.current !== null && ref.current.deadline === deadline ? ref.current.span : null;
}

/**
 * The board's right half when there is no map: the selected track's reading, at
 * the height a four-stage reveal actually needs.
 *
 * It draws the same `TrackReading` the right rail draws, because there must not
 * be two of it. What it adds is room — at the 1280 floor this column is the full
 * height of the pane against the rail's 203 px of scroll, and the rail's copy
 * stands down while this one is up (`TrackPanel`'s `readingInPane`).
 */
function ReadingPane(props: {
  readonly track: MwTrackWire | null;
  readonly serverNowMs: number;
}): JSX.Element {
  return (
    <div
      data-testid="mw-reading"
      data-mat="panel"
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        background: 'var(--surface)',
      }}
    >
      <div
        data-mat="chrome hdr"
        style={{
          flex: 'none',
          height: 33,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '0 12px',
          background: 'var(--surface-2)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <span style={{ ...DATA_LABEL, flex: 'none', color: 'var(--text)' }}>READING</span>
        {props.track !== null && (
          <span
            data-testid="mw-reading-id"
            style={{
              ...DATA,
              flex: 1,
              minWidth: 0,
              fontWeight: 400,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: 'var(--text-muted)',
            }}
          >
            {props.track.eventId}
          </span>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {props.track === null ? (
          <div
            data-testid="mw-reading-empty"
            style={{ ...LABEL, padding: '20px 12px', textAlign: 'center' }}
          >
            NO TRACK SELECTED
          </div>
        ) : (
          <TrackReading track={props.track} serverNowMs={props.serverNowMs} />
        )}
      </div>
    </div>
  );
}
